import { useCallback, useRef, useState } from "react";
import { useScribe, CommitStrategy } from "@elevenlabs/react";
import { supabase } from "@/integrations/supabase/client";

export type VoiceStatus = "idle" | "starting" | "listening" | "stopping" | "error";

interface Options {
  onTranscript: (text: string) => void; // called with each committed segment
  onError?: (msg: string) => void;
}

export function useVoiceTranscribe({ onTranscript, onError }: Options) {
  const [status, setStatus] = useState<VoiceStatus>("idle");
  const [partial, setPartial] = useState("");
  const partialRef = useRef("");

  const scribe = useScribe({
    modelId: "scribe_v2_realtime",
    commitStrategy: CommitStrategy.VAD,
    onPartialTranscript: (data: any) => {
      const txt = data?.text ?? "";
      partialRef.current = txt;
      setPartial(txt);
    },
    onCommittedTranscript: (data: any) => {
      const txt = (data?.text ?? "").trim();
      if (txt) onTranscript(txt);
      partialRef.current = "";
      setPartial("");
    },
  });

  const start = useCallback(async () => {
    if (status === "listening" || status === "starting") return;
    setStatus("starting");
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) {
        throw new Error("Please sign in to use voice input.");
      }
      const { data, error } = await supabase.functions.invoke(
        "elevenlabs-scribe-token",
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      if (error) throw new Error(error.message || "Token request failed");
      if (!data?.token) throw new Error("No token received");

      await scribe.connect({
        token: data.token,
        microphone: { echoCancellation: true, noiseSuppression: true },
      });
      setStatus("listening");
    } catch (e: any) {
      setStatus("error");
      const msg =
        e?.name === "NotAllowedError"
          ? "Microphone permission denied. Enable it in your browser to use voice input."
          : e?.message || "Could not start voice input.";
      onError?.(msg);
      setTimeout(() => setStatus("idle"), 1500);
    }
  }, [scribe, status, onError]);

  const stop = useCallback(async () => {
    if (status !== "listening") return;
    setStatus("stopping");
    try {
      await scribe.disconnect();
    } catch {}
    setStatus("idle");
    setPartial("");
    partialRef.current = "";
  }, [scribe, status]);

  const toggle = useCallback(() => {
    if (status === "listening") void stop();
    else void start();
  }, [status, start, stop]);

  return { status, partial, start, stop, toggle, isListening: status === "listening" };
}
